import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import {
  selectStudioTool,
  type StudioTool,
  type StudioToolGroup,
} from '@okrapdf/pdf-studio-plugin/studio';
import { BrandLogo } from '@/lib/brand/logo';
import { isAppHost } from '@/lib/host';
import { capture } from '@/lib/posthog';
import { cn } from '@/lib/utils';
import { TOOLS_BY_SLUG } from './data';
import { publicSlugForStudioTool, studioToolIdForSlug } from './studio-tool-id';
import { matchTools } from './tool-catalogue';

/**
 * Global ⌘K / Ctrl+K tool palette (VS Code quick-open / Linear pattern):
 * one shortcut from anywhere opens a search over the tool catalogue All tools
 * shows, arrow keys move, Enter opens the tool, and recent picks surface first.
 * Mounted once at the app root by tools-app, which passes the catalogue and,
 * where the Agent can answer, what to do with a query no tool matches. Trigger
 * buttons call openToolCommandPalette(). When a Studio workbench is mounted, a
 * pick switches its active tool in place (rail-click semantics); otherwise it
 * navigates to the tool: its public page, or this host's workbench for a tool
 * with none.
 */

const OPEN_EVENT = 'okra:open-tool-palette';
const RECENTS_KEY = 'okra:recent-tool-slugs';
const MAX_RECENTS = 5;
const ASK_AGENT_OPTION_ID = 'tool-palette-option-ask-agent';

export type ToolPaletteSource = 'keyboard' | 'marketing_header' | 'studio_landing';

/** Open the palette from anywhere (header buttons, landing actions). */
export function openToolCommandPalette(source: ToolPaletteSource = 'keyboard') {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { source } }));
}

/** A pick is remembered by its public slug, which says which of two pages it was, else its tool id. */
function recentToolId(pick: string): string {
  return studioToolIdForSlug(pick) ?? pick;
}

function readRecents(): string[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s): s is string => typeof s === 'string');
  } catch {
    return [];
  }
}

function pushRecent(pick: string): void {
  try {
    const toolId = recentToolId(pick);
    const others = readRecents().filter((s) => recentToolId(s) !== toolId);
    localStorage.setItem(RECENTS_KEY, JSON.stringify([pick, ...others].slice(0, MAX_RECENTS)));
  } catch {
    /* localStorage unavailable */
  }
}

/** Where a tool opens when no Studio is mounted to take it. */
function toolRoute(toolId: string, slug: string | null): string {
  if (slug) return `/tools/${slug}`;
  return `${isAppHost() ? '/tools' : '/studio'}?tool=${encodeURIComponent(toolId)}`;
}

interface PaletteRow {
  tool: StudioTool;
  nameIndices: number[];
  /** The `/tools/<slug>` page this row opens, or null for a tool with none. */
  slug: string | null;
}

interface PaletteGroup {
  label: string;
  rows: PaletteRow[];
}

const IS_MAC =
  typeof navigator !== 'undefined' && /mac/i.test(navigator.platform ?? '');

export function ToolCommandPalette({
  catalogue,
  onAsk,
}: {
  catalogue: StudioToolGroup[];
  /** Takes a query no tool matches. Without it the palette says nothing was found. */
  onAsk?: (text: string) => void;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [recents, setRecents] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Global ⌘K / Ctrl+K toggle + programmatic opens from trigger buttons.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((prev) => {
          if (!prev) capture('tool_palette_opened', { source: 'keyboard' });
          return !prev;
        });
      }
    };
    const onOpen = (e: Event) => {
      const source = (e as CustomEvent<{ source?: string }>).detail?.source;
      setOpen((prev) => {
        if (!prev) capture('tool_palette_opened', { source: source ?? 'button' });
        return true;
      });
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_EVENT, onOpen);
    };
  }, []);

  // Reset state on every open so the palette always starts clean.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIndex(0);
    setRecents(readRecents());
    inputRef.current?.focus();
  }, [open]);

  const groupTitles = useMemo(
    () =>
      new Map(
        catalogue.flatMap((group) => group.tools.map((tool) => [tool.id, group.title] as const)),
      ),
    [catalogue],
  );

  const groups = useMemo<PaletteGroup[]>(() => {
    const q = query.trim();
    if (q) {
      const rows = matchTools(
        q,
        catalogue.flatMap((group) => group.tools),
      ).map((m) => ({
        tool: m.tool,
        nameIndices: m.nameIndices,
        slug: m.publicSlug,
      }));
      return rows.length ? [{ label: 'Tools', rows }] : [];
    }
    const byId = new Map(catalogue.flatMap((group) => group.tools).map((t) => [t.id, t]));
    const result: PaletteGroup[] = [];
    const recentRows = new Map<string, PaletteRow>();
    for (const pick of recents) {
      const tool = byId.get(recentToolId(pick));
      if (tool && !recentRows.has(tool.id)) {
        const slug = pick in TOOLS_BY_SLUG ? pick : publicSlugForStudioTool(tool.id);
        recentRows.set(tool.id, { tool, nameIndices: [], slug });
      }
    }
    if (recentRows.size) result.push({ label: 'Recent', rows: [...recentRows.values()] });
    for (const group of catalogue) {
      const rows = group.tools
        .filter((t) => !recentRows.has(t.id))
        .map((tool) => ({
          tool,
          nameIndices: [],
          slug: publicSlugForStudioTool(tool.id),
        }));
      if (rows.length) result.push({ label: group.title, rows });
    }
    return result;
  }, [catalogue, query, recents]);

  // Flat navigable list; activeIndex addresses this, not the grouped view.
  const flatRows = useMemo(() => groups.flatMap((g) => g.rows), [groups]);

  const askText = onAsk && flatRows.length === 0 ? query.trim() : '';

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Keep the active option visible while arrowing through the list.
  useEffect(() => {
    if (!open || !flatRows.length) return;
    const row = flatRows[Math.min(activeIndex, flatRows.length - 1)];
    document
      .getElementById(`tool-palette-option-${row.tool.id}`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, flatRows, open]);

  const close = useCallback(() => setOpen(false), []);

  const runTool = useCallback(
    ({ tool, slug }: PaletteRow) => {
      pushRecent(slug ?? tool.id);
      capture('tool_palette_tool_selected', {
        // The public slug and category where the tool has a page, as before W.4.132.
        tool: slug ?? tool.id,
        category: slug ? TOOLS_BY_SLUG[slug].category : tool.category,
        has_query: query.trim().length > 0,
      });
      setOpen(false);
      // When a Studio workbench is mounted, switch its active tool in place
      // (same semantics as a rail click or an All tools tile) instead of
      // routing away from it. Falls back to the tool's route when no Studio is
      // mounted or it rejected the selection (unknown / hidden on this host).
      if (selectStudioTool(tool.id)) return;
      navigate(toolRoute(tool.id, slug));
    },
    [navigate, query],
  );

  const ask = useCallback(
    (text: string) => {
      setOpen(false);
      onAsk?.(text);
    },
    [onAsk],
  );

  const onPanelKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (flatRows.length ? (i + 1) % flatRows.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) =>
        flatRows.length ? (i - 1 + flatRows.length) % flatRows.length : 0,
      );
    } else if (e.key === 'Enter') {
      // Enter while composing confirms the IME candidate; it picks nothing.
      if (e.nativeEvent.isComposing) return;
      e.preventDefault();
      const row = flatRows[activeIndex];
      if (row) runTool(row);
      else if (askText) ask(askText);
    }
  };

  if (!open || typeof document === 'undefined') return null;

  let optionIndex = -1;
  const activeTool = flatRows[activeIndex]?.tool;
  const activeOptionId = activeTool
    ? `tool-palette-option-${activeTool.id}`
    : askText
      ? ASK_AGENT_OPTION_ID
      : undefined;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[100] bg-foreground/40 backdrop-blur-sm"
        onClick={close}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search tools"
        className="fixed left-1/2 top-[12%] z-[101] w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-2xl bg-popover text-popover-foreground shadow-float ring-1 ring-border"
        onKeyDown={onPanelKeyDown}
      >
        <div className="flex items-center gap-2 border-b border-border px-4">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          {/* eslint-disable-next-line okra-ui/no-raw-primitives -- combobox ARIA pattern needs a raw input */}
          <input
            ref={inputRef}
            role="combobox"
            aria-expanded="true"
            aria-controls="tool-palette-list"
            aria-activedescendant={activeOptionId}
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tools…"
            className="h-12 w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
          <kbd className="shrink-0 rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            esc
          </kbd>
        </div>

        <div
          id="tool-palette-list"
          role="listbox"
          aria-label="Tools"
          className="max-h-[50vh] overflow-y-auto p-2"
        >
          {askText ? (
            <div
              id={ASK_AGENT_OPTION_ID}
              role="option"
              aria-selected="true"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => ask(askText)}
              // ph-no-capture: autocapture would send the row's text with the click.
              className="ph-no-capture flex cursor-pointer items-center gap-3 rounded-lg bg-accent px-3 py-2 text-accent-foreground"
            >
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border bg-card">
                <BrandLogo decorative size={16} className="rounded-sm" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">Ask Okra Agent</span>
                {/* Masked in session replay: this is a prompt. No aria-label for the same
                    reason, as replay records attributes. */}
                <span data-ph-mask className="block truncate text-xs text-muted-foreground">
                  {askText}
                </span>
              </span>
            </div>
          ) : groups.length === 0 ? (
            <p data-ph-mask className="px-3 py-8 text-center text-sm text-muted-foreground">
              No tools found for “{query.trim()}”
            </p>
          ) : (
            groups.map((group) => (
              <div key={group.label}>
                <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {group.label}
                </p>
                {group.rows.map((row) => {
                  optionIndex += 1;
                  const rowIndex = optionIndex;
                  const isActive = rowIndex === activeIndex;
                  const Icon = row.tool.icon;
                  return (
                    <div
                      key={row.tool.id}
                      id={`tool-palette-option-${row.tool.id}`}
                      role="option"
                      aria-selected={isActive}
                      aria-label={row.tool.title}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseEnter={() => setActiveIndex(rowIndex)}
                      onClick={() => runTool(row)}
                      className={cn(
                        'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2',
                        isActive
                          ? 'bg-accent text-accent-foreground'
                          : 'text-foreground',
                      )}
                    >
                      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border bg-card text-muted-foreground">
                        <Icon className="h-4 w-4" aria-hidden="true" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">
                          <HighlightedName
                            name={row.tool.title}
                            indices={row.nameIndices}
                          />
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {row.tool.description}
                        </span>
                      </span>
                      <span className="shrink-0 text-[11px] text-muted-foreground">
                        {groupTitles.get(row.tool.id)}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="flex items-center gap-4 border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
          <span>
            <kbd className="rounded border border-border bg-muted px-1 py-0.5">↑↓</kbd>{' '}
            navigate
          </span>
          <span>
            <kbd className="rounded border border-border bg-muted px-1 py-0.5">↵</kbd>{' '}
            open
          </span>
          <span>
            <kbd className="rounded border border-border bg-muted px-1 py-0.5">esc</kbd>{' '}
            close
          </span>
          <span className="ml-auto hidden sm:inline">
            {IS_MAC ? '⌘' : 'Ctrl'}+K to toggle
          </span>
        </div>
      </div>
    </>,
    document.body,
  );
}

function HighlightedName({
  name,
  indices,
}: {
  name: string;
  indices: number[];
}) {
  if (!indices.length) return <>{name}</>;
  const matched = new Set(indices);
  return (
    <>
      {name.split('').map((ch, i) =>
        matched.has(i) ? (
          <mark key={i} className="bg-transparent font-semibold text-primary">
            {ch}
          </mark>
        ) : (
          <span key={i}>{ch}</span>
        ),
      )}
    </>
  );
}
